<script>
    /**
     * Dialog — modern floating modal shell.
     *
     * Successor to floating-modal/FloatingModal.svelte. Dark surface, Inter
     * type, subtle borders — matches the manage settings surfaces. Content is
     * supplied via snippets so it stays generic; ConfirmDialog builds the
     * common confirm/cancel flow on top of this.
     *
     * Props:
     *  - title:            heading text (optional)
     *  - status:           null | 'info' | 'warning' | 'danger' — tints the
     *                      leading icon badge
     *  - icon:             material icon override (defaults per status)
     *  - size:             'small' | 'medium' | 'large' — max width
     *  - hasCloseButton:   show the × button (default true)
     *  - closeOnBackdrop:  clicking the backdrop closes (default true)
     *  - onclose:          called for ×, backdrop click, and Escape
     *  - children:         body snippet
     *  - footer:           footer snippet (actions row)
     */
    import { onMount } from 'svelte';
    import { fade, scale } from 'svelte/transition';
    import { getFocusableElements, focusOnFirst } from '@repo/config/Utils';

    let {
        title = null,
        status = null,
        icon = null,
        size = 'small',
        hasCloseButton = true,
        closeOnBackdrop = true,
        onclose = null,
        children = null,
        footer = null,
    } = $props();

    const STATUS_ICONS = {
        info: 'info',
        warning: 'warning',
        danger: 'error',
    };

    const statusIcon = $derived(icon || (status ? STATUS_ICONS[status] : null));

    function close() {
        onclose?.();
    }

    function handleBackdropClick(e) {
        if (closeOnBackdrop && e.target === e.currentTarget) close();
    }

    function handleKeydown(e) {
        if (e.key === 'Escape') {
            e.stopPropagation();
            close();
            return;
        }
        if (e.key !== 'Tab') return;

        // Keep tab focus inside the dialog.
        const focusable = getFocusableElements('.bz-dialog');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }

    onMount(() => {
        setTimeout(() => focusOnFirst('.bz-dialog'), 0);
    });
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="bz-dialog-backdrop" onclick={handleBackdropClick} onkeydown={handleKeydown} transition:fade={{ duration: 120 }}>
    <div
        class="bz-dialog size-{size}"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        aria-labelledby={title ? 'bz_dialog_title' : null}
        transition:scale={{ duration: 140, start: 0.96 }}
    >
        {#if title || statusIcon || hasCloseButton}
            <header class="bz-dialog-head">
                {#if statusIcon}
                    <span class="status-badge {status || 'neutral'}">
                        <span class="material-icons">{statusIcon}</span>
                    </span>
                {/if}
                {#if title}
                    <h2 id="bz_dialog_title">{title}</h2>
                {/if}
                <div class="spacer"></div>
                {#if hasCloseButton}
                    <button type="button" class="close-btn" aria-label="Close dialog" onclick={close}>
                        <span class="material-icons">close</span>
                    </button>
                {/if}
            </header>
        {/if}

        {#if children}
            <div class="bz-dialog-body">
                {@render children()}
            </div>
        {/if}

        {#if footer}
            <footer class="bz-dialog-foot">
                {@render footer()}
            </footer>
        {/if}
    </div>
</div>

<style>
    .bz-dialog-backdrop {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        background: rgba(0, 0, 0, 0.65);
        backdrop-filter: blur(2px);
        z-index: 9999;
    }

    .bz-dialog {
        display: flex;
        flex-direction: column;
        width: 100%;
        background: #191919;
        border: 1px solid rgba(255, 255, 255, 0.09);
        border-radius: 14px;
        box-shadow: 0 18px 50px rgba(0, 0, 0, 0.55);
        font-family: 'Inter', sans-serif;
        color: #fff;
        outline: none;
    }
    .size-small { max-width: 420px; }
    .size-medium { max-width: 560px; }
    .size-large { max-width: 760px; }

    .bz-dialog-head {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px 16px 0 18px;
    }
    .bz-dialog-head h2 {
        margin: 0;
        font-size: 15px;
        font-weight: 700;
        letter-spacing: 0.01em;
        color: #fff;
    }
    .spacer {
        flex: 1;
    }

    .status-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        flex: none;
        border-radius: 10px;
    }
    .status-badge .material-icons {
        font-size: 20px;
    }
    .status-badge.neutral {
        background: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.7);
    }
    .status-badge.info {
        background: rgba(74, 198, 255, 0.14);
        color: #4ac6ff;
    }
    .status-badge.warning {
        background: rgba(242, 163, 64, 0.14);
        color: #f2a340;
    }
    .status-badge.danger {
        background: rgba(255, 100, 100, 0.14);
        color: #ff6464;
    }

    .close-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        padding: 0;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: rgba(255, 255, 255, 0.45);
        cursor: pointer;
        transition: background 0.12s, color 0.12s;
    }
    .close-btn:hover {
        background: #2b2b2b;
        color: #fff;
    }
    .close-btn .material-icons {
        font-size: 19px;
    }

    .bz-dialog-body {
        padding: 14px 18px 4px;
        font-size: 13.5px;
        line-height: 1.55;
        color: rgba(255, 255, 255, 0.75);
    }
    .bz-dialog-body :global(p) {
        margin: 0 0 10px;
    }
    .bz-dialog-body :global(p:last-child) {
        margin-bottom: 0;
    }
    .bz-dialog-body :global(b),
    .bz-dialog-body :global(strong) {
        color: #fff;
        font-weight: 600;
    }

    .bz-dialog-foot {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
        padding: 16px 18px 18px;
    }
</style>
